import {
  Dropdown,
  NumberInput,
  RadioButton,
  RadioButtonGroup,
  TextArea,
  TextInput,
} from '@carbon/react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { FormModal } from '../components/FormModal';
import { asProblem, problemMessage } from '../problems';
import { ApprovalDialog } from './ApprovalDialog';
import { CustomerFields } from './CustomerFields';
import { DraftLines, ItemPicker } from './ItemPicker';
import {
  customerOrNull,
  emptyCustomer,
  isCancelled,
  ORDER_PERMISSIONS,
  ordersKey,
  toLineRequest,
  useApprovalFlow,
  useFormat,
  useOrderSetup,
  useOrdersApi,
  type LineDraft,
} from './model';
import { newKey, type CustomerInfo, type Delivery, type OrderDetail, type OrderLine, type Payment } from './ordersApi';

interface Option {
  id: string;
  label: string;
}

export interface DialogProps {
  order: OrderDetail;
  onClose(): void;
  /** A short confirmation for the page, e.g. the change to give. */
  onDone?(message: string): void;
}

/** A dialog around one change: the form, the server's error in the user's language, and a busy primary button. */
function ActionModal({
  title,
  label,
  size = 'sm',
  primaryText,
  danger,
  disabled,
  busy,
  error,
  onSubmit,
  onClose,
  children,
}: {
  title: string;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  primaryText: string;
  danger?: boolean;
  disabled?: boolean;
  busy: boolean;
  error: unknown;
  onSubmit(): void;
  onClose(): void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const problem = error && !isCancelled(error) ? asProblem(error) : null;
  return (
    <FormModal
      title={title}
      label={label}
      size={size}
      primaryText={primaryText}
      danger={danger}
      disabled={disabled}
      pending={busy}
      error={problem ? problemMessage(t, problem) : null}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      {children}
    </FormModal>
  );
}

/** After a change the order, its history and payments are reloaded. */
function useOrderChange<T>(fn: () => Promise<T>, onSuccess: (result: T) => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ordersKey });
      onSuccess(result);
    },
  });
}

function OptionDropdown({ id, label, options, value, onChange }: { id: string; label: string; options: Option[]; value?: string; onChange(id: string): void }) {
  const { t } = useTranslation();
  return (
    <Dropdown<Option>
      id={id}
      titleText={label}
      label={t('admin.choose')}
      items={options}
      itemToString={(o) => o?.label ?? ''}
      selectedItem={options.find((o) => o.id === value)}
      onChange={({ selectedItem }) => onChange(selectedItem?.id ?? '')}
    />
  );
}

export function AddItemsDialog({ order, onClose }: DialogProps) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const [drafts, setDrafts] = useState<LineDraft[]>([]);
  const save = useOrderChange(() => api.addLines(order.summary, drafts.map(toLineRequest)), onClose);
  return (
    <ActionModal
      size="lg"
      title={t('orders.actions.addItems')}
      label={order.summary.orderNumber}
      primaryText={t('orders.create.add')}
      disabled={drafts.length === 0}
      busy={save.isPending}
      error={save.error}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <div className="pos-orders__compose">
        <ItemPicker saleTypeId={order.summary.saleTypeId} onAdd={(draft) => setDrafts((d) => [...d, draft])} />
        <DraftLines drafts={drafts} onChange={setDrafts} />
      </div>
    </ActionModal>
  );
}

export function EditLineDialog({ order, line, onClose }: DialogProps & { line: OrderLine }) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const [quantity, setQuantity] = useState(Number(line.quantity));
  const [note, setNote] = useState(line.note ?? '');
  const save = useOrderChange(() => api.updateLine(order.summary, line.id, { quantity, note: note || null, modifierIds: null }), onClose);
  return (
    <ActionModal
      title={t('orders.actions.editLine')}
      label={line.name}
      primaryText={t('admin.save')}
      disabled={quantity <= 0}
      busy={save.isPending}
      error={save.error}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <NumberInput
        id="edit-line-quantity"
        label={t('orders.lines.quantity')}
        min={1}
        max={9999}
        value={quantity}
        onChange={(_e, { value }) => setQuantity(Number(value) || 0)}
      />
      <TextInput id="edit-line-note" labelText={t('orders.lines.note')} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
    </ActionModal>
  );
}

/** Voids a line or the whole order: a reason, and a manager's approval when the user lacks the right (FR-ORD-24/25). */
export function VoidDialog({ order, line, onClose }: DialogProps & { line?: OrderLine }) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const { reasons } = useOrderSetup();
  const approval = useApprovalFlow();
  const [reasonCodeId, setReasonCodeId] = useState<string>();
  const [note, setNote] = useState('');
  const unsent = line?.status === 'new';
  const save = useOrderChange(
    () =>
      line
        ? approval.run(
            { actionCode: ORDER_PERMISSIONS.lineVoid, targetType: 'order_line', targetId: line.id, summary: t('orders.approval.voidLine', { name: line.name }) },
            (approvalId) => api.voidLine(order.summary, line.id, { reasonCodeId: reasonCodeId ?? null, approvalId, note: note || null }),
          )
        : approval.run(
            {
              actionCode: ORDER_PERMISSIONS.orderVoid,
              targetType: 'order',
              targetId: order.summary.id,
              summary: t('orders.approval.voidOrder', { number: order.summary.orderNumber }),
            },
            (approvalId) => api.voidOrder(order.summary, { reasonCodeId: reasonCodeId ?? null, approvalId, note: note || null }),
          ),
    onClose,
  );
  return (
    <>
      <ActionModal
        title={t(line ? 'orders.void.lineTitle' : 'orders.void.orderTitle')}
        label={line?.name ?? order.summary.orderNumber}
        primaryText={t(line ? 'orders.actions.voidLine' : 'orders.actions.voidOrder')}
        danger
        disabled={!unsent && !reasonCodeId}
        busy={save.isPending}
        error={save.error}
        onSubmit={() => save.mutate()}
        onClose={onClose}
      >
        {(unsent || !line) && <p>{t(unsent ? 'orders.void.unsentHint' : 'orders.void.orderHint')}</p>}
        {!unsent && <OptionDropdown id="void-reason" label={t('orders.void.reason')} options={reasons('void')} value={reasonCodeId} onChange={setReasonCodeId} />}
        <TextInput id="void-note" labelText={t('orders.void.note')} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      </ActionModal>
      <ApprovalDialog pending={approval.pending} />
    </>
  );
}

/** A preset or a typed discount on the order or one line; above the user's limit a manager approves (FR-DSC-01). */
export function DiscountDialog({ order, onClose }: DialogProps) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const { setup, reasons } = useOrderSetup();
  const approval = useApprovalFlow();
  const [presetId, setPresetId] = useState('');
  const [name, setName] = useState('');
  const [method, setMethod] = useState<'percent' | 'amount'>('percent');
  const [value, setValue] = useState<number | null>(null);
  const [lineId, setLineId] = useState('');
  const [reasonCodeId, setReasonCodeId] = useState('');
  const preset = setup?.discountPresets.find((p) => p.id === presetId);
  const typed = !preset || preset.value == null;
  const presets: Option[] = [{ id: '', label: t('orders.discounts.manual') }, ...(setup?.discountPresets ?? []).map((p) => ({ id: p.id, label: p.name }))];
  const targets: Option[] = [
    { id: '', label: t('orders.discounts.wholeOrder') },
    ...order.lines.filter((l) => l.status !== 'voided').map((l) => ({ id: l.id, label: `${String(l.quantity)} × ${l.name}` })),
  ];
  const save = useOrderChange(
    () =>
      approval.run(
        {
          actionCode: ORDER_PERMISSIONS.discount,
          targetType: 'order',
          targetId: order.summary.id,
          summary: t('orders.approval.discount', { number: order.summary.orderNumber }),
        },
        (approvalId) =>
          api.addDiscount(order.summary, {
            orderLineId: lineId || null,
            presetId: presetId || null,
            name: preset ? null : name || null,
            method: preset ? null : method,
            value: typed ? value : null,
            reasonCodeId: reasonCodeId || null,
            approvalId,
          }),
      ),
    onClose,
  );
  return (
    <>
      <ActionModal
        title={t('orders.actions.addDiscount')}
        label={order.summary.orderNumber}
        primaryText={t('orders.actions.addDiscount')}
        disabled={typed && (!value || (!preset && !name))}
        busy={save.isPending}
        error={save.error}
        onSubmit={() => save.mutate()}
        onClose={onClose}
      >
        <OptionDropdown id="discount-preset" label={t('orders.discounts.preset')} options={presets} value={presetId} onChange={setPresetId} />
        {!preset && (
          <>
            <TextInput id="discount-name" labelText={t('orders.discounts.name')} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
            <RadioButtonGroup legendText={t('orders.discounts.method')} name="discount-method" valueSelected={method} onChange={(v) => setMethod(v as 'percent' | 'amount')}>
              <RadioButton id="discount-percent" value="percent" labelText={t('orders.discounts.percent')} />
              <RadioButton id="discount-amount" value="amount" labelText={t('orders.discounts.amount')} />
            </RadioButtonGroup>
          </>
        )}
        {typed && (
          <NumberInput
            id="discount-value"
            label={t('orders.discounts.value')}
            min={0}
            max={method === 'percent' && !preset ? 100 : undefined}
            allowEmpty
            value={value ?? ''}
            hideSteppers
            onChange={(_e, { value: v }) => setValue(v === '' ? null : Number(v))}
          />
        )}
        <OptionDropdown id="discount-target" label={t('orders.discounts.appliesTo')} options={targets} value={lineId} onChange={setLineId} />
        <OptionDropdown
          id="discount-reason"
          label={t('orders.discounts.reason')}
          options={[{ id: '', label: t('admin.none') }, ...reasons('discount')]}
          value={reasonCodeId}
          onChange={setReasonCodeId}
        />
      </ActionModal>
      <ApprovalDialog pending={approval.pending} />
    </>
  );
}

export function ChargeDialog({ order, onClose }: DialogProps) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const { setup, name } = useOrderSetup();
  const [chargeTypeId, setChargeTypeId] = useState('');
  const options = (setup?.chargeTypes ?? []).map((c) => ({ id: c.id, label: name(c.id, c.name) }));
  const save = useOrderChange(() => api.addCharge(order.summary, chargeTypeId), onClose);
  return (
    <ActionModal
      title={t('orders.actions.addCharge')}
      label={order.summary.orderNumber}
      primaryText={t('orders.actions.addCharge')}
      disabled={!chargeTypeId}
      busy={save.isPending}
      error={save.error}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <OptionDropdown id="charge-type" label={t('orders.charges.type')} options={options} value={chargeTypeId} onChange={setChargeTypeId} />
    </ActionModal>
  );
}

const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const date = new Date(iso);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

export function DetailsDialog({ order, onClose }: DialogProps) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const address = (order.deliveryAddress ?? {}) as Partial<CustomerInfo>;
  const [customer, setCustomer] = useState<CustomerInfo>({ ...emptyCustomer, ...address });
  const [guestCount, setGuestCount] = useState<number | null>(order.summary.guestCount == null ? null : Number(order.summary.guestCount));
  const [promisedAt, setPromisedAt] = useState(toLocalInput(order.summary.promisedAt));
  const [customerNote, setCustomerNote] = useState(order.customerNote ?? '');
  const [kitchenNote, setKitchenNote] = useState(order.kitchenNote ?? '');
  const save = useOrderChange(
    () =>
      api.updateDetails(order.summary, {
        guestCount,
        customer: customerOrNull(customer),
        promisedAt: promisedAt ? new Date(promisedAt).toISOString() : null,
        customerNote: customerNote || null,
        kitchenNote: kitchenNote || null,
      }),
    onClose,
  );
  return (
    <ActionModal
      size="md"
      title={t('orders.actions.editDetails')}
      label={order.summary.orderNumber}
      primaryText={t('admin.save')}
      busy={save.isPending}
      error={save.error}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <NumberInput
        id="details-guests"
        label={t('orders.details.guests')}
        min={1}
        max={999}
        allowEmpty
        value={guestCount ?? ''}
        onChange={(_e, { value }) => setGuestCount(value === '' ? null : Number(value))}
      />
      <CustomerFields idPrefix="details" value={customer} onChange={setCustomer} withAddress />
      <TextInput id="details-promised" type="datetime-local" labelText={t('orders.details.promisedAt')} value={promisedAt} onChange={(e) => setPromisedAt(e.target.value)} />
      <TextArea id="details-customer-note" labelText={t('orders.details.customerNote')} rows={2} maxCount={1000} value={customerNote} onChange={(e) => setCustomerNote(e.target.value)} />
      <TextArea id="details-kitchen-note" labelText={t('orders.details.kitchenNote')} rows={2} maxCount={1000} value={kitchenNote} onChange={(e) => setKitchenNote(e.target.value)} />
    </ActionModal>
  );
}

export function SaleTypeDialog({ order, onClose }: DialogProps) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const { setup, saleTypeName } = useOrderSetup();
  const [saleTypeId, setSaleTypeId] = useState(order.summary.saleTypeId);
  const save = useOrderChange(() => api.changeSaleType(order.summary, saleTypeId), onClose);
  return (
    <ActionModal
      title={t('orders.saleType.title')}
      label={order.summary.orderNumber}
      primaryText={t('admin.save')}
      disabled={saleTypeId === order.summary.saleTypeId}
      busy={save.isPending}
      error={save.error}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <p>{t('orders.saleType.hint')}</p>
      <RadioButtonGroup legendText={t('orders.create.saleType')} name="change-sale-type" orientation="vertical" valueSelected={saleTypeId} onChange={(v) => setSaleTypeId(String(v))}>
        {(setup?.saleTypes ?? []).map((s) => (
          <RadioButton key={s.id} id={`change-sale-type-${s.id}`} value={s.id} labelText={saleTypeName(s.id, s.code)} />
        ))}
      </RadioButtonGroup>
    </ActionModal>
  );
}

/** Records a manual tender (cash, transfer, card slip). Card terminal and QR tenders are taken at the POS. */
export function PaymentDialog({ order, onClose, onDone }: DialogProps) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const format = useFormat();
  const { setup, name } = useOrderSetup();
  const currency = order.summary.currencyCode;
  const methods = (setup?.paymentMethods ?? []).filter((m) => !m.requiresHo);
  const [methodId, setMethodId] = useState(methods[0]?.id ?? '');
  const [amount, setAmount] = useState<number | null>(Number(order.summary.balance));
  const [tendered, setTendered] = useState<number | null>(null);
  const [reference, setReference] = useState('');
  const [key] = useState(newKey);
  const method = methods.find((m) => m.id === methodId);
  const save = useOrderChange(
    () =>
      api.recordPayment(order.summary.id, {
        paymentMethodId: methodId,
        amount: amount ?? 0,
        tenderedAmount: method?.allowsChange && tendered ? tendered : null,
        reference: reference || null,
        idempotencyKey: key,
      }),
    (payment) => {
      if (Number(payment.changeAmount) > 0) onDone?.(t('orders.payments.changeDue', { amount: format.money(payment.changeAmount, currency) }));
      onClose();
    },
  );
  return (
    <ActionModal
      title={t('orders.actions.recordPayment')}
      label={`${order.summary.orderNumber} · ${t('orders.payments.balanceDue', { amount: format.money(order.summary.balance, currency) })}`}
      primaryText={t('orders.actions.recordPayment')}
      disabled={!methodId || !amount || amount <= 0 || (method?.requiresReference && !reference)}
      busy={save.isPending}
      error={save.error}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <OptionDropdown id="payment-method" label={t('orders.payments.method')} options={methods.map((m) => ({ id: m.id, label: name(m.id, m.name) }))} value={methodId} onChange={setMethodId} />
      <NumberInput id="payment-amount" label={t('orders.payments.amount')} min={0} allowEmpty hideSteppers value={amount ?? ''} onChange={(_e, { value }) => setAmount(value === '' ? null : Number(value))} />
      {method?.allowsChange && (
        <NumberInput id="payment-tendered" label={t('orders.payments.tendered')} min={0} allowEmpty hideSteppers value={tendered ?? ''} onChange={(_e, { value }) => setTendered(value === '' ? null : Number(value))} />
      )}
      {method?.requiresReference && (
        <TextInput id="payment-reference" labelText={t('orders.payments.reference')} value={reference} maxLength={100} onChange={(e) => setReference(e.target.value)} />
      )}
    </ActionModal>
  );
}

/** All or part of a payment back, as a new record; above the user's limit a manager approves (FR-PAY-13). */
export function RefundDialog({ order, payment, onClose }: DialogProps & { payment: Payment }) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const format = useFormat();
  const { reasons } = useOrderSetup();
  const approval = useApprovalFlow();
  const refundable = Number(payment.refundable);
  const [amount, setAmount] = useState<number | null>(refundable);
  const [reasonCodeId, setReasonCodeId] = useState('');
  const [key] = useState(newKey);
  const shown = format.money(amount ?? 0, payment.currencyCode);
  const save = useOrderChange(
    () =>
      approval.run(
        { actionCode: ORDER_PERMISSIONS.refund, targetType: 'payment', targetId: payment.id, summary: t('orders.approval.refund', { amount: shown }) },
        (approvalId) => api.refund(payment.id, { amount: amount ?? 0, reasonCodeId: reasonCodeId || null, approvalId, idempotencyKey: key }),
      ),
    onClose,
  );
  return (
    <>
      <ActionModal
        title={t('orders.refund.title')}
        label={order.summary.orderNumber}
        primaryText={t('orders.actions.refund')}
        danger
        disabled={!amount || amount <= 0 || amount > refundable || !reasonCodeId}
        busy={save.isPending}
        error={save.error}
        onSubmit={() => save.mutate()}
        onClose={onClose}
      >
        <p>{t('orders.refund.hint')}</p>
        <NumberInput
          id="refund-amount"
          label={t('orders.refund.amount')}
          helperText={t('orders.refund.max', { amount: format.money(refundable, payment.currencyCode) })}
          min={0}
          max={refundable}
          allowEmpty
          hideSteppers
          value={amount ?? ''}
          onChange={(_e, { value }) => setAmount(value === '' ? null : Number(value))}
        />
        <OptionDropdown id="refund-reason" label={t('orders.refund.reason')} options={reasons('refund')} value={reasonCodeId} onChange={setReasonCodeId} />
      </ActionModal>
      <ApprovalDialog pending={approval.pending} />
    </>
  );
}

/** Sends a delivery order out with the store's own driver or a provider (ADR 0009). */
export function DispatchDialog({ order, onClose }: DialogProps) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const [method, setMethod] = useState<'own_driver' | 'provider'>('own_driver');
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [providerCode, setProviderCode] = useState('');
  const [providerName, setProviderName] = useState('');
  const [providerReference, setProviderReference] = useState('');
  const known = useQuery({ queryKey: [...ordersKey, 'shipping-providers'], queryFn: () => api.shippingProviders() });
  const [trackingUrl, setTrackingUrl] = useState('');
  const [fee, setFee] = useState<number | null>(null);
  const save = useOrderChange(
    () =>
      api.createDelivery(order.summary.id, {
        method,
        driverUserId: null,
        driverName: driverName || null,
        driverPhone: driverPhone || null,
        providerCode: method === 'provider' ? providerCode || null : null,
        providerName: method === 'provider' ? providerName || null : null,
        providerReference: method === 'provider' ? providerReference || null : null,
        trackingUrl: trackingUrl || null,
        fee,
        address: null,
      }),
    onClose,
  );
  return (
    <ActionModal
      size="md"
      title={t('orders.actions.dispatch')}
      label={order.summary.orderNumber}
      primaryText={t('orders.actions.dispatch')}
      disabled={method === 'provider' && !providerCode}
      busy={save.isPending}
      error={save.error}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <RadioButtonGroup legendText={t('orders.delivery.method')} name="dispatch-method" valueSelected={method} onChange={(v) => setMethod(v as 'own_driver' | 'provider')}>
        <RadioButton id="dispatch-own" value="own_driver" labelText={t('orders.delivery.methods.own_driver')} />
        <RadioButton id="dispatch-provider" value="provider" labelText={t('orders.delivery.methods.provider')} />
      </RadioButtonGroup>
      {method === 'provider' && (
        <>
          {(known.data ?? []).length > 0 && (
            <OptionDropdown
              id="dispatch-known-provider"
              label={t('orders.delivery.knownProvider')}
              options={(known.data ?? []).map((p) => ({ id: p.code, label: p.name }))}
              value={providerCode}
              onChange={(code) => {
                setProviderCode(code);
                setProviderName(known.data?.find((p) => p.code === code)?.name ?? '');
              }}
            />
          )}
          <TextInput id="dispatch-provider-name" labelText={t('orders.delivery.providerName')} value={providerName} maxLength={200} onChange={(e) => setProviderName(e.target.value)} />
          <TextInput id="dispatch-provider-code" labelText={t('orders.delivery.providerCode')} value={providerCode} maxLength={100} onChange={(e) => setProviderCode(e.target.value)} />
          <TextInput id="dispatch-provider-ref" labelText={t('orders.delivery.providerReference')} value={providerReference} onChange={(e) => setProviderReference(e.target.value)} />
        </>
      )}
      <TextInput id="dispatch-driver" labelText={t('orders.delivery.driverName')} value={driverName} maxLength={200} onChange={(e) => setDriverName(e.target.value)} />
      <TextInput id="dispatch-driver-phone" type="tel" labelText={t('orders.delivery.driverPhone')} value={driverPhone} maxLength={30} onChange={(e) => setDriverPhone(e.target.value)} />
      <TextInput id="dispatch-tracking" type="url" labelText={t('orders.delivery.trackingUrl')} value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} />
      <NumberInput id="dispatch-fee" label={t('orders.delivery.fee')} min={0} allowEmpty hideSteppers value={fee ?? ''} onChange={(_e, { value }) => setFee(value === '' ? null : Number(value))} />
    </ActionModal>
  );
}

export function DeliveryStatusDialog({ order, delivery, onClose }: DialogProps & { delivery: Delivery }) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const options = delivery.nextStatuses.map((s) => ({ id: s, label: t(`orders.delivery.statuses.${s}` as I18nKey) }));
  const [status, setStatus] = useState(options[0]?.id ?? '');
  const [failureReason, setFailureReason] = useState('');
  const [note, setNote] = useState('');
  const [driverName, setDriverName] = useState(delivery.driverName ?? '');
  const [driverPhone, setDriverPhone] = useState(delivery.driverPhone ?? '');
  const save = useOrderChange(
    () =>
      api.deliveryStatus(delivery, {
        status,
        note: note || null,
        failureReason: status === 'failed' ? failureReason || null : null,
        driverName: driverName || null,
        driverPhone: driverPhone || null,
      }),
    onClose,
  );
  return (
    <ActionModal
      title={t('orders.actions.updateStatus')}
      label={order.summary.orderNumber}
      primaryText={t('admin.save')}
      disabled={!status || (status === 'failed' && !failureReason)}
      busy={save.isPending}
      error={save.error}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <OptionDropdown id="delivery-status" label={t('orders.delivery.newStatus')} options={options} value={status} onChange={setStatus} />
      {status === 'failed' && (
        <TextInput id="delivery-failure" labelText={t('orders.delivery.failureReason')} value={failureReason} maxLength={500} onChange={(e) => setFailureReason(e.target.value)} />
      )}
      {status === 'assigned' && (
        <>
          <TextInput id="delivery-driver" labelText={t('orders.delivery.driverName')} value={driverName} onChange={(e) => setDriverName(e.target.value)} />
          <TextInput id="delivery-driver-phone" type="tel" labelText={t('orders.delivery.driverPhone')} value={driverPhone} onChange={(e) => setDriverPhone(e.target.value)} />
        </>
      )}
      <TextInput id="delivery-note" labelText={t('orders.delivery.note')} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
    </ActionModal>
  );
}
